import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServiceExecutionSequenceModel } from 'app/app-model/diagnostic-service/service-execution-sequence.model';
import { FlashModel } from 'app/app-model/flash/flash.model';
import { CategoryServiceBase } from 'app/app-services/category-base.service';
import { DiagnosticServiceProvider } from 'app/app-services/diagnostic.service.service';
import { FlashCategoryItem } from 'app/data-categories/flash/flash-category-item';
import { Observable, of } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';

import { ApiProxyService } from '../modules/shared/services/api-proxy.service';
import { SpecificationService } from './specification-service';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class FlashService extends CategoryServiceBase<FlashModel, FlashCategoryItem> {

  constructor(private diagnosticServiceProvider: DiagnosticServiceProvider,
    private specificationService: SpecificationService,
    private locationService: Location,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    protected apiProxy: ApiProxyService,
    protected configService: ConfigService) {
    super(apiProxy, configService);
    this.apiCategoryName = 'flash';
  }

  getItemsPreview(specificationVersionId: number): Observable<FlashCategoryItem[]> {
    this.isPending = true;
    if (this.hasCachedItems) {
      this.isPending = false;
      return of(this.items);
    } else {
      return this.apiProxy.get<FlashModel[]>(`${this.configService.apiUrl}versions/${specificationVersionId}/flash`).pipe(map(flashItems => {
        this.items = new Array<FlashCategoryItem>();
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < flashItems.length; i++) {
          const flashItem = new FlashCategoryItem(this.specificationService, this.locationService, this.router, this.activatedRoute);
          flashItem.flashService = this;
          flashItem.diagnosticServiceProvider = this.diagnosticServiceProvider;
          flashItem.model = flashItems[i];

          this.items.push(flashItem);
        }

        this.hasCachedItems = true;

        return this.items;
      }), finalize(() => {
        this.isPending = false;
        this.getItemsPreviewDone.next(this.isPending);
      }));
    }
  }

  createItem(specificationVersionId: number, itemName?: string): Observable<FlashCategoryItem> {
    const itemToCreate = new FlashModel();
    itemToCreate.name = itemName;
    itemToCreate.setSequence = new ServiceExecutionSequenceModel();
    return this.apiProxy.post<FlashModel>(`${this.configService.apiUrl}versions/${specificationVersionId}/flash/`, itemToCreate).pipe(map(item => {
      const flashCategoryItem = new FlashCategoryItem(this.specificationService, this.locationService, this.router, this.activatedRoute);
      flashCategoryItem.flashService = this;
      flashCategoryItem.diagnosticServiceProvider = this.diagnosticServiceProvider;
      flashCategoryItem.isPreview = false;
      flashCategoryItem.model = item;

      this.items.push(flashCategoryItem);

      return flashCategoryItem;
    }));
  }

  getCategoryItemModel(specificationVersionId: number, name: string): Observable<FlashCategoryItem> {
    return this.apiProxy.get<FlashModel>(`${this.configService.apiUrl}versions/${specificationVersionId}/flash/${name}/`).pipe(map(item => {
      const flashCategoryItem = new FlashCategoryItem(this.specificationService, this.locationService, this.router, this.activatedRoute);
      flashCategoryItem.flashService = this;
      flashCategoryItem.diagnosticServiceProvider = this.diagnosticServiceProvider;
      flashCategoryItem.isPreview = false;
      flashCategoryItem.model = item;

      this.items.push(flashCategoryItem);
      return flashCategoryItem;
    }));
  }

  getCompleteItems(specificationVersionId: number): Observable<FlashCategoryItem[]> {
    throw new Error('Method not implemented.');
  }

  deleteItem(specificationVersionId: number, itemName?: string): Observable<any> {
    return this.apiProxy.delete(`${this.configService.apiUrl}versions/${specificationVersionId}/flash/${itemName}`)
      .pipe(tap(deletedItem => this.removeCachedFlashItem(deletedItem.name)));
  }

  updateItem(specificationVersionId: number, flashDataModel: any, itemName?: string): Observable<FlashModel> {
    return this.apiProxy.put(`${this.configService.apiUrl}versions/${specificationVersionId}/flash/${itemName}/`, flashDataModel);
  }

  public getFlashItem(specificationVersionId: number, itemName: string): Observable<FlashCategoryItem> {
    return this.apiProxy.get<FlashCategoryItem>(`${this.configService.apiUrl}versions/${specificationVersionId}/flash/${itemName}/`);
  }

  public reset() {
    this.items.forEach(item => item.destroy());
    super.reset();
  }

  private removeCachedFlashItem(removedItemName: string) {
    const itemToRemoveFromCache = this.items.find(item => item.model.name === removedItemName);

    if (itemToRemoveFromCache) {
      itemToRemoveFromCache.destroy();
    }

    this.removeCachedItem(removedItemName);
  }
}
